import { useState } from 'react'
import { ConfirmModal } from '@/components/ConfirmModal'
import type { LookupTypeOption } from '@/lib/lookupTypes'
import type { AcceptanceDefaults, DocumentTypeField } from '@/types/bundle'
import {
  createBlankField,
  createSyntheticFieldKey,
  duplicateField,
  isSyntheticFieldKey,
  resolveAcceptanceDefault,
} from '../fieldUtils'
import { FieldRow } from './FieldRow'

export interface FieldsTabProps {
  fields: Record<string, DocumentTypeField>
  onChange: (fields: Record<string, DocumentTypeField>) => void
  entryDefaults?: Partial<AcceptanceDefaults>
  globalDefaults: AcceptanceDefaults
  serverErrors?: Record<string, Record<string, string>>
  lookupTypeOptions: LookupTypeOption[]
  /** Field keys (from resolveFieldsForSave) whose response_tag isn't
   * snake_case (PRD 34) - Save is already blocked while this is non-empty,
   * this just tells the offending row(s) which one to visually flag. */
  invalidKeys?: string[]
}

export function FieldsTab({
  fields,
  onChange,
  entryDefaults,
  globalDefaults,
  serverErrors,
  lookupTypeOptions,
  invalidKeys,
}: FieldsTabProps) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  // Keys created via Add/Duplicate this session - plays FieldRow's entrance
  // animation once on mount; never needs cleanup since a CSS animation
  // doesn't replay on later re-renders once already applied.
  const [bornKeys, setBornKeys] = useState<Set<string>>(new Set())

  function setExpanded(key: string, isExpanded: boolean) {
    setExpandedKeys((current) => {
      const next = new Set(current)
      if (isExpanded) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function updateField(key: string, updater: (field: DocumentTypeField) => DocumentTypeField) {
    onChange({ ...fields, [key]: updater(fields[key]) })
  }

  function handleAddField() {
    const key = createSyntheticFieldKey()
    onChange({ ...fields, [key]: createBlankField() })
    setExpanded(key, true)
    setBornKeys((current) => new Set(current).add(key))
  }

  function handleDuplicateField(sourceKey: string) {
    const key = createSyntheticFieldKey()
    onChange({ ...fields, [key]: duplicateField(fields[sourceKey]) })
    setExpanded(key, true)
    setBornKeys((current) => new Set(current).add(key))
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return
    const next = { ...fields }
    delete next[deleteTarget]
    onChange(next)
    setExpanded(deleteTarget, false)
    setDeleteTarget(null)
  }

  const fieldKeys = Object.keys(fields)

  return (
    <div className="flex flex-col gap-4">
      {fieldKeys.map((key) => {
        const field = fields[key]
        return (
          <FieldRow
            key={key}
            fieldKey={key}
            field={field}
            isLocked={!isSyntheticFieldKey(key)}
            onChange={(next) => updateField(key, () => next)}
            onDelete={() => setDeleteTarget(key)}
            onDuplicate={() => handleDuplicateField(key)}
            expanded={expandedKeys.has(key)}
            onToggleExpanded={() => setExpanded(key, !expandedKeys.has(key))}
            resolvedDefault={resolveAcceptanceDefault(field, entryDefaults, globalDefaults)}
            serverErrors={serverErrors?.[key]}
            isNew={bornKeys.has(key)}
            lookupTypeOptions={lookupTypeOptions}
            isInvalidName={invalidKeys?.includes(key) ?? false}
          />
        )
      })}

      <button
        type="button"
        onClick={handleAddField}
        className="w-fit rounded-control border border-border px-4 py-2 text-body font-medium text-text-primary hover:bg-surface-raised"
      >
        + Add Field
      </button>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete field"
        description="This field will be removed from the draft. It is only persisted on your next Save."
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
